import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, where, getDocs, doc, updateDoc } from 'firebase/firestore';
import StoreParcelReceipt from './StoreParcelReceipt';
import { isStoreParcelOrder } from '../utils/orderType';

// Store-to-store parcels reach this same "Handed over to Courier" state as
// regular customer orders (same SteadFastHandOver/status fields), but they
// never carry stock-based fields like packagingComplete/ready_for_pickup —
// so we identify them with isStoreParcelOrder (orderType + SPORD orderId
// prefix) instead.
const StoreParcelOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchPhone, setSearchPhone] = useState('');
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [selectedOrderForReceipt, setSelectedOrderForReceipt] = useState(null);
  // 'processing' = not yet packaged (active queue, like Order Packaging)
  // 'completed'  = packagingComplete && ready_for_pickup already true
  const [activeTab, setActiveTab] = useState('processing');
  // Tracks whether the open receipt should show the "Confirm & Complete"
  // button (opened from Processing) or just Print/Close (opened from Completed)
  const [receiptMode, setReceiptMode] = useState('view');

  useEffect(() => {
    fetchStoreParcelOrders();
  }, []);

  const fetchStoreParcelOrders = async () => {
    try {
      setLoading(true);
      const ordersRef = collection(db, 'orders');

      // Filter on SteadFastHandOver/status at the query level (same as the
      // other tabs), then filter to parcels client-side with
      // isStoreParcelOrder — this way a parcel order still shows up here
      // even in the rare case its orderType field is missing but its
      // orderId still carries the SPORD prefix.
      const q = query(
        ordersRef,
        where('SteadFastHandOver', '==', true),
        where('status', '==', 'Handed over to Courier')
      );

      const querySnapshot = await getDocs(q);
      const ordersData = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        if (!isStoreParcelOrder(data)) {
          return;
        }
        ordersData.push({
          id: doc.id,
          ...data
        });
      });

      ordersData.sort((a, b) => {
        const dateA = a.created_at?.toDate ? a.created_at.toDate() : new Date(a.created_at || 0);
        const dateB = b.created_at?.toDate ? b.created_at.toDate() : new Date(b.created_at || 0);
        return dateB - dateA;
      });

      setOrders(ordersData);
    } catch (error) {
      console.error('Error fetching store parcel orders:', error);
      alert('Failed to fetch store parcel orders: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const isPackagedComplete = (order) =>
    order.packagingComplete === true && order.ready_for_pickup === true;

  const processingOrders = orders.filter((order) => !isPackagedComplete(order));
  const completedOrders = orders.filter((order) => isPackagedComplete(order));

  const tabOrders = activeTab === 'processing' ? processingOrders : completedOrders;

  const filteredOrders = searchPhone.trim()
    ? tabOrders.filter((order) => order.customerInfo?.phone?.includes(searchPhone.trim()))
    : tabOrders;

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const toggleOrderDetails = (orderId) => {
    setExpandedOrderId(expandedOrderId === orderId ? null : orderId);
  };

  // mode: 'process' (from Processing tab, shows Confirm & Complete) or
  // 'view' (from Completed tab, reprint only)
  const handlePrintReceipt = (order, mode = 'view') => {
    setReceiptMode(mode);
    setSelectedOrderForReceipt(order);
  };

  const handleCloseReceipt = () => {
    setSelectedOrderForReceipt(null);
    setReceiptMode('view');
  };

  // Parcels don't come from stock_basket, so unlike regular orders
  // (BarcodeScanner.js) there's nothing to scan or delete from stock —
  // this just marks the parcel as packaged and ready for pickup.
  const handleConfirmComplete = async (orderId) => {
    try {
      const orderRef = doc(db, 'orders', orderId);
      await updateDoc(orderRef, {
        packagingComplete: true,
        packagingCompletedAt: new Date(),
        ready_for_pickup: true
      });
      setSelectedOrderForReceipt(null);
      setReceiptMode('view');
      await fetchStoreParcelOrders();
    } catch (error) {
      console.error('Error completing parcel packaging:', error);
      alert('Failed to complete packaging: ' + error.message);
    }
  };

  // A parcel item is either a single product { itemType: 'product', name,
  // quantity, weight, image } or a box { itemType: 'box', label, quantity,
  // weight, contents: [{ name, quantity, image }] }. Neither carries a
  // price, unlike regular cartItems.
  const renderParcelItem = (item, index) => {
    if (item.itemType === 'box') {
      return (
        <div key={index} className="bg-white p-3 rounded-lg border">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-semibold text-gray-900">📦 {item.label || 'Box'}</div>
            <div className="text-xs text-gray-500">{item.weight ? `${item.weight} kg` : ''}</div>
          </div>
          {item.contents?.length > 0 ? (
            <div className="space-y-1 pl-2 border-l-2 border-gray-200">
              {item.contents.map((c, i) => (
                <div key={i} className="text-xs text-gray-600 flex items-center gap-2">
                  {c.image && (
                    <img
                      src={c.image}
                      alt={c.name}
                      className="w-8 h-8 object-cover rounded"
                      onError={(e) => { e.target.src = 'https://via.placeholder.com/32'; }}
                    />
                  )}
                  <span>{c.name} × {c.quantity}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-xs text-gray-400">কোনো contents তালিকাভুক্ত নেই</div>
          )}
        </div>
      );
    }

    // itemType === 'product'
    return (
      <div key={index} className="flex items-center gap-3 bg-white p-3 rounded-lg border">
        {item.image && (
          <img
            src={item.image}
            alt={item.name}
            className="w-16 h-16 object-cover rounded"
            onError={(e) => { e.target.src = 'https://via.placeholder.com/64'; }}
          />
        )}
        <div className="flex-1">
          <div className="text-sm font-medium text-gray-900">{item.name}</div>
          <div className="text-xs text-gray-500">
            Qty: {item.quantity}{item.weight ? ` · ${item.weight} kg` : ''}
          </div>
        </div>
      </div>
    );
  };

  if (selectedOrderForReceipt) {
    return (
      <StoreParcelReceipt
        order={selectedOrderForReceipt}
        onClose={handleCloseReceipt}
        onConfirmComplete={receiptMode === 'process' ? handleConfirmComplete : undefined}
      />
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-2xl font-semibold text-gray-700">Loading store parcels...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="bg-white shadow-md">
        <div className="px-8 py-6">
          <h2 className="text-2xl font-bold text-gray-800">Store Parcels</h2>
          <p className="text-sm text-gray-600 mt-1">Store-to-store parcels handed over to courier</p>
        </div>
      </div>

      <div className="px-8 py-6">
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('processing')}
            className={`px-5 py-2 rounded-lg font-semibold transition duration-200 ${activeTab === 'processing'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
              }`}
          >
            Processing ({processingOrders.length})
          </button>
          <button
            onClick={() => setActiveTab('completed')}
            className={`px-5 py-2 rounded-lg font-semibold transition duration-200 ${activeTab === 'completed'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
              }`}
          >
            Completed ({completedOrders.length})
          </button>
        </div>

        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Search & Filter</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Receiving Store Phone
              </label>
              <input
                type="text"
                value={searchPhone}
                onChange={(e) => setSearchPhone(e.target.value)}
                placeholder="Search by phone..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="flex items-end gap-2">
              <button
                onClick={() => setSearchPhone('')}
                className="flex-1 bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
              >
                Clear Filters
              </button>
              <button
                onClick={fetchStoreParcelOrders}
                className="flex-1 bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
              >
                Refresh
              </button>
            </div>
          </div>
          <div className="mt-4 text-sm text-gray-600">
            Showing {filteredOrders.length} of {tabOrders.length} {activeTab} store parcels
          </div>
        </div>

        {filteredOrders.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <p className="text-gray-600 text-lg">
              {tabOrders.length === 0
                ? activeTab === 'processing'
                  ? 'No parcels waiting to be packaged'
                  : 'No completed parcels found'
                : 'No parcels match your search criteria'}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Receiving Store
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Order ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Items
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredOrders.map((order) => (
                  <React.Fragment key={order.id}>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {order.customerInfo?.name || 'N/A'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {order.customerInfo?.phone || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 font-mono">
                          {order.orderId || order.id}
                        </div>
                        <div className="text-xs text-gray-500">
                          {order.steadfastConsignmentId || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {order.cartItems?.length || 0} item(s)
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-600">
                          {formatDate(order.created_at)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {activeTab === 'processing' ? (
                          <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                            ⏳ Processing
                          </span>
                        ) : (
                          <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800">
                            🚚 {order.status || 'N/A'}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={() => toggleOrderDetails(order.id)}
                            className="text-blue-600 hover:text-blue-900 font-medium px-3 py-1 rounded border border-blue-600 hover:bg-blue-50"
                          >
                            {expandedOrderId === order.id ? 'Hide' : 'View'}
                          </button>
                          {activeTab === 'processing' ? (
                            <button
                              onClick={() => handlePrintReceipt(order, 'process')}
                              className="bg-green-500 hover:bg-green-600 text-white font-medium px-3 py-1 rounded flex items-center gap-1"
                            >
                              📦 Process
                            </button>
                          ) : (
                            <button
                              onClick={() => handlePrintReceipt(order, 'view')}
                              className="bg-green-500 hover:bg-green-600 text-white font-medium px-3 py-1 rounded flex items-center gap-1"
                            >
                              🖨️ Print
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>

                    {expandedOrderId === order.id && (
                      <tr>
                        <td colSpan="6" className="px-6 py-4 bg-gray-50">
                          <div className="space-y-4">
                            <h4 className="font-semibold text-gray-800 text-lg">Parcel Details</h4>

                            <div>
                              <h5 className="font-medium text-gray-700 mb-2">Items:</h5>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {order.cartItems?.map((item, index) => renderParcelItem(item, index))}
                              </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="bg-white p-4 rounded-lg border">
                                <h5 className="font-medium text-gray-700 mb-2">Receiving Store Information:</h5>
                                <div className="space-y-1 text-sm">
                                  <p><span className="font-medium">Name:</span> {order.customerInfo?.name || 'N/A'}</p>
                                  <p><span className="font-medium">Phone:</span> {order.customerInfo?.phone || 'N/A'}</p>
                                  <p><span className="font-medium">Address:</span> {order.customerInfo?.address || 'N/A'}</p>
                                  {order.customerInfo?.notes && (
                                    <p><span className="font-medium">Notes:</span> {order.customerInfo.notes}</p>
                                  )}
                                </div>
                              </div>

                              <div className="bg-white p-4 rounded-lg border">
                                <h5 className="font-medium text-gray-700 mb-2">Delivery Information:</h5>
                                <div className="space-y-1 text-sm">
                                  <p><span className="font-medium">Delivery Charge (COD):</span> ৳{order.totalAmount ?? 'N/A'}</p>
                                  <p><span className="font-medium">Delivery Area:</span> {order.deliveryArea || 'N/A'}</p>
                                  <p><span className="font-medium">Total Weight:</span> {order.totalWeight ?? 'N/A'} kg</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default StoreParcelOrders;